/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Derived status for the latest usage poll state. Omitted when no config is set for the provider.
 */
export const LastPollStatus = {
  Pending: "pending",
  Success: "success",
  Failed: "failed",
} as const;
/**
 * Derived status for the latest usage poll state. Omitted when no config is set for the provider.
 */
export type LastPollStatus = OpenEnum<typeof LastPollStatus>;

/**
 * Per-organization AI provider integration config. The provider API key is write-only; reads only expose whether a key is configured.
 */
export type AIIntegrationConfig = {
  /**
   * How the provider org is billed: 'metered' (pay-per-token; dashboard cost is real spend), 'flat_rate' (subscription seats; cost is an estimate), or 'unknown'. Empty/omitted when not declared.
   */
  billingMode?: string | undefined;
  /**
   * ISO 8601 timestamp when the config was created. Omitted when no config is set.
   */
  createdAt?: Date | undefined;
  /**
   * Whether the provider integration is active.
   */
  enabled: boolean;
  /**
   * Provider scope identifier. Required for anthropic_compliance and codex_compliance (organization id) and chatgpt_compliance (ChatGPT workspace UUID); omitted for providers that do not need one.
   */
  externalOrganizationId?: string | undefined;
  /**
   * Whether an API key is currently stored. The key itself is never returned.
   */
  hasApiKey: boolean;
  /**
   * Config ID. Omitted when no config is set for the provider.
   */
  id?: string | undefined;
  /**
   * Stored error from the latest failed usage poll. Omitted unless the latest poll state failed.
   */
  lastPollError?: string | undefined;
  /**
   * ISO 8601 timestamp for the latest failed usage poll. Omitted unless a poll has failed.
   */
  lastPollFailedAt?: Date | undefined;
  /**
   * Derived status for the latest usage poll state. Omitted when no config is set for the provider.
   */
  lastPollStatus?: LastPollStatus | undefined;
  /**
   * ISO 8601 timestamp for the last successful usage poll. Omitted until a poll succeeds.
   */
  lastPolledAt?: Date | undefined;
  /**
   * ISO 8601 timestamp for the next scheduled usage poll. Omitted when no config is set.
   */
  nextPollAfter?: Date | undefined;
  /**
   * Organization the config belongs to.
   */
  organizationId: string;
  /**
   * Project used as the telemetry write target. Omitted when no config is set.
   */
  projectId?: string | undefined;
  /**
   * AI provider identifier. Supported values include cursor, anthropic_compliance, codex_compliance, and chatgpt_compliance.
   */
  provider: string;
  /**
   * ISO 8601 timestamp of the most recent change. Omitted when no config is set.
   */
  updatedAt?: Date | undefined;
};

/** @internal */
export const LastPollStatus$inboundSchema: z.ZodMiniType<
  LastPollStatus,
  unknown
> = openEnums.inboundSchema(LastPollStatus);

/** @internal */
export const AIIntegrationConfig$inboundSchema: z.ZodMiniType<
  AIIntegrationConfig,
  unknown
> = z.pipe(
  z.object({
    billing_mode: z.optional(z.string()),
    created_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    enabled: z.boolean(),
    external_organization_id: z.optional(z.string()),
    has_api_key: z.boolean(),
    id: z.optional(z.string()),
    last_poll_error: z.optional(z.string()),
    last_poll_failed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_poll_status: z.optional(LastPollStatus$inboundSchema),
    last_polled_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    next_poll_after: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    organization_id: z.string(),
    project_id: z.optional(z.string()),
    provider: z.string(),
    updated_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "billing_mode": "billingMode",
      "created_at": "createdAt",
      "external_organization_id": "externalOrganizationId",
      "has_api_key": "hasApiKey",
      "last_poll_error": "lastPollError",
      "last_poll_failed_at": "lastPollFailedAt",
      "last_poll_status": "lastPollStatus",
      "last_polled_at": "lastPolledAt",
      "next_poll_after": "nextPollAfter",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "updated_at": "updatedAt",
    });
  }),
);

export function aiIntegrationConfigFromJSON(
  jsonString: string,
): SafeParseResult<AIIntegrationConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AIIntegrationConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AIIntegrationConfig' from JSON`,
  );
}
